import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { useState, useEffect } from 'react'
import { useAppSelector } from '@redux/hooks/useAppState'
import { Theme } from '@/theme'
import { brandColorsOKLCH } from '@/utils/preciseBrandColors'

interface ColorValues {
	l: number
	c: number
	h: number
}

/**
 * Color customizer component that allows users to control theme colors
 */
export const ColorCustomizer = () => {
	const theme = useAppSelector((state) => state.theme.theme)
	const isDarkMode = theme === Theme.Dark

	// Parse OKLCH values from CSS custom properties
	const parseOKLCH = (oklchString: string): ColorValues => {
		const match = oklchString.match(/oklch\(([\d.]+)%?\s+([\d.]+)\s+([\d.]+)\)/)
		if (match) {
			return {
				l: parseFloat(match[1]) / 100,
				c: parseFloat(match[2]),
				h: parseFloat(match[3])
			}
		}
		return { l: 0.45, c: 0.15, h: 303 }
	}

	// Initialize with brand colors
	const [primaryColor, setPrimaryColor] = useState<ColorValues>(() => 
		parseOKLCH(brandColorsOKLCH.primaryAction)
	)
	
	const [backgroundColor, setBackgroundColor] = useState<ColorValues>(() => 
		isDarkMode 
			? parseOKLCH(brandColorsOKLCH.primary)
			: parseOKLCH(brandColorsOKLCH.primaryContrast)
	)

	// Apply colors to CSS custom properties
	useEffect(() => {
		const root = document.documentElement
		
		// Update primary color
		root.style.setProperty('--primary', `oklch(${primaryColor.l} ${primaryColor.c} ${primaryColor.h})`)
		root.style.setProperty('--accent', `oklch(${primaryColor.l} ${primaryColor.c} ${primaryColor.h})`)
		
		// Update secondary to be a lighter version of primary
		root.style.setProperty('--secondary', `oklch(${Math.min(primaryColor.l + 0.1, 0.95)} ${primaryColor.c * 0.6} ${primaryColor.h})`)
		
		// Update background color
		if (!isDarkMode) {
			root.style.setProperty('--background', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
			root.style.setProperty('--card', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
			root.style.setProperty('--popover', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
		} else {
			// In dark mode, use the selected color as background
			root.style.setProperty('--background', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
			root.style.setProperty('--card', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
			root.style.setProperty('--popover', `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})`)
			
			// Update muted to be slightly lighter
			root.style.setProperty('--muted', `oklch(${Math.min(backgroundColor.l + 0.05, 0.4)} ${backgroundColor.c} ${backgroundColor.h})`)
		}
		
		// Update component colors for dynamic manipulation
		root.style.setProperty('--primary-action-l', `${primaryColor.l * 100}%`)
		root.style.setProperty('--primary-action-c', primaryColor.c.toString())
		root.style.setProperty('--primary-action-h', primaryColor.h.toString())
		
	}, [primaryColor, backgroundColor, isDarkMode])

	// Preset colors
	const presetColors = [
		{ name: 'HoneyGrid Purple', l: 0.4101, c: 0.1697, h: 306.81 },
		{ name: 'Ocean Blue', l: 0.45, c: 0.15, h: 240 },
		{ name: 'Forest Green', l: 0.45, c: 0.15, h: 150 },
		{ name: 'Sunset Orange', l: 0.55, c: 0.18, h: 40 },
		{ name: 'Ruby Red', l: 0.45, c: 0.2, h: 20 },
		{ name: 'Royal Purple', l: 0.4, c: 0.18, h: 280 },
	]

	const backgroundPresets = [
		{ name: 'Pure White', l: 1, c: 0, h: 0 },
		{ name: 'Brand White', l: 0.9993, c: 0.0011, h: 81.04 },
		{ name: 'Warm Cream', l: 0.98, c: 0.01, h: 90 },
		{ name: 'Cool Gray', l: 0.98, c: 0.005, h: 250 },
		{ name: 'Soft Pink', l: 0.98, c: 0.01, h: 350 },
	]

	const darkBackgroundPresets = [
		{ name: 'Brand Dark', l: 0.2452, c: 0.0197, h: 19.57 },
		{ name: 'Pure Black', l: 0.1, c: 0, h: 0 },
		{ name: 'Navy', l: 0.2, c: 0.05, h: 250 },
		{ name: 'Dark Purple', l: 0.2, c: 0.05, h: 280 },
		{ name: 'Dark Green', l: 0.2, c: 0.03, h: 150 },
	]

	const resetToDefaults = () => {
		setPrimaryColor(parseOKLCH(brandColorsOKLCH.primaryAction))
		setBackgroundColor(
			isDarkMode 
				? parseOKLCH(brandColorsOKLCH.primary)
				: parseOKLCH(brandColorsOKLCH.primaryContrast)
		)
	}

	return (
		<Card className="p-6 space-y-6">
			<div>
				<h2 className="text-2xl font-semibold mb-2">Color Customizer</h2>
				<p className="text-muted-foreground">
					Customize the primary and background colors. Changes apply to the entire app!
				</p>
			</div>

			{/* Primary Color Controls */}
			<div className="space-y-4">
				<h3 className="text-lg font-medium">Primary Color</h3>
				
				{/* Color preview */}
				<div className="flex items-center gap-4">
					<div 
						className="w-20 h-20 rounded-lg shadow-md"
						style={{ backgroundColor: `oklch(${primaryColor.l} ${primaryColor.c} ${primaryColor.h})` }}
					/>
					<div className="text-sm space-y-1">
						<p>L: {(primaryColor.l * 100).toFixed(1)}%</p>
						<p>C: {primaryColor.c.toFixed(3)}</p>
						<p>H: {primaryColor.h.toFixed(1)}°</p>
					</div>
				</div>

				{/* Sliders */}
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Lightness</Label>
						<Slider
							value={[primaryColor.l * 100]}
							onValueChange={([value]) => setPrimaryColor({ ...primaryColor, l: value / 100 })}
							min={20}
							max={80}
							step={1}
						/>
					</div>
					<div className="space-y-2">
						<Label>Chroma (Saturation)</Label>
						<Slider
							value={[primaryColor.c * 100]}
							onValueChange={([value]) => setPrimaryColor({ ...primaryColor, c: value / 100 })}
							min={0}
							max={30}
							step={0.1}
						/>
					</div>
					<div className="space-y-2">
						<Label>Hue</Label>
						<Slider
							value={[primaryColor.h]}
							onValueChange={([value]) => setPrimaryColor({ ...primaryColor, h: value })}
							min={0}
							max={360}
							step={1}
						/>
					</div>
				</div>

				{/* Preset colors */}
				<div className="space-y-2">
					<Label>Presets</Label>
					<div className="grid grid-cols-3 gap-2">
						{presetColors.map((preset) => (
							<Button
								key={preset.name}
								variant="outline"
								size="sm"
								className="justify-start"
								onClick={() => setPrimaryColor({ l: preset.l, c: preset.c, h: preset.h })}
							>
								<div 
									className="w-4 h-4 rounded mr-2"
									style={{ backgroundColor: `oklch(${preset.l} ${preset.c} ${preset.h})` }}
								/>
								{preset.name}
							</Button>
						))}
					</div>
				</div>
			</div>

			{/* Background Color Controls */}
			<div className="space-y-4">
				<h3 className="text-lg font-medium">Background Color</h3>
				
				{/* Color preview */}
				<div className="flex items-center gap-4">
					<div 
						className="w-20 h-20 rounded-lg shadow-md border"
						style={{ backgroundColor: `oklch(${backgroundColor.l} ${backgroundColor.c} ${backgroundColor.h})` }}
					/>
					<div className="text-sm space-y-1">
						<p>L: {(backgroundColor.l * 100).toFixed(1)}%</p>
						<p>C: {backgroundColor.c.toFixed(3)}</p>
						<p>H: {backgroundColor.h.toFixed(1)}°</p>
					</div>
				</div>

				{/* Sliders */}
				<div className="space-y-4">
					<div className="space-y-2">
						<Label>Lightness</Label>
						<Slider
							value={[backgroundColor.l * 100]}
							onValueChange={([value]) => setBackgroundColor({ ...backgroundColor, l: value / 100 })}
							min={isDarkMode ? 10 : 90}
							max={isDarkMode ? 40 : 100}
							step={0.1}
						/>
					</div>
					<div className="space-y-2">
						<Label>Chroma (Tint)</Label>
						<Slider
							value={[backgroundColor.c * 100]}
							onValueChange={([value]) => setBackgroundColor({ ...backgroundColor, c: value / 100 })}
							min={0}
							max={5}
							step={0.01}
						/>
					</div>
					<div className="space-y-2">
						<Label>Hue</Label>
						<Slider
							value={[backgroundColor.h]}
							onValueChange={([value]) => setBackgroundColor({ ...backgroundColor, h: value })}
							min={0}
							max={360}
							step={1}
						/>
					</div>
				</div>

				{/* Preset backgrounds */}
				<div className="space-y-2">
					<Label>Presets</Label>
					<div className="grid grid-cols-2 gap-2">
						{(isDarkMode ? darkBackgroundPresets : backgroundPresets).map((preset) => (
							<Button
								key={preset.name}
								variant="outline"
								size="sm"
								className="justify-start"
								onClick={() => setBackgroundColor({ l: preset.l, c: preset.c, h: preset.h })}
							>
								<div 
									className="w-4 h-4 rounded mr-2 border"
									style={{ backgroundColor: `oklch(${preset.l} ${preset.c} ${preset.h})` }}
								/>
								{preset.name}
							</Button>
						))}
					</div>
				</div>
			</div>

			{/* Reset button */}
			<Button onClick={resetToDefaults} variant="outline" className="w-full">
				Reset to Brand Colors
			</Button>

			{/* Info */}
			<div className="text-sm text-muted-foreground space-y-1">
				<p>• Changes apply to the entire application</p>
				<p>• Secondary colors are automatically derived from the primary</p>
				<p>• Dark mode adjusts colors for optimal contrast</p>
			</div>
		</Card>
	)
}